/**
 * Dashboard chrome: sidebar + main canvas.
 *
 * The selected website ID is owned at this level and passed down via
 * context, so every nested page picks up changes from the sidebar
 * picker without prop drilling.
 *
 * Mobile layout: below ``md`` (768px) the sidebar slides off-screen
 * and is summoned via a top bar's hamburger button. Clicking any
 * link or the dimmed backdrop dismisses it. The desktop layout
 * (≥ md) is unchanged — sidebar is always visible, no top bar.
 */

import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext.jsx";
import LanguageSwitcher from "../../components/LanguageSwitcher.jsx";
import TokenBalance from "../../components/TokenBalance.jsx";
import WebsitePicker from "../../components/WebsitePicker.jsx";

const SelectedWebsiteContext = createContext(null);
export const useSelectedWebsite = () => useContext(SelectedWebsiteContext);

const SECTIONS = [
  { key: "overview", path: "/dashboard", end: true },
  { key: "answers", path: "/dashboard/websites/:id/answers" },
  { key: "sources", path: "/dashboard/websites/:id/sources" },
  { key: "competitors", path: "/dashboard/websites/:id/competitors" },
  { key: "prompts", path: "/dashboard/websites/:id/prompts" },
  { key: "schedule", path: "/dashboard/websites/:id/schedule" },
  { key: "ga4", path: "/dashboard/websites/:id/integrations/ga4" },
  { key: "llmsTxt", path: "/dashboard/websites/:id/llms-txt" },
  { key: "websites", path: "/dashboard/websites" },
  { key: "promptVolume", path: "/dashboard/prompt-volume" },
  { key: "billing", path: "/dashboard/billing" },
  { key: "account", path: "/dashboard/account" },
];

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [websiteId, setWebsiteId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-close the mobile sidebar after every route change. Without
  // this the slide-out panel stays draped over the page the user
  // just clicked into.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // ESC dismisses the mobile sidebar — standard a11y for any
  // modal-like overlay.
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  function onLogout() {
    logout();
    navigate("/login");
  }

  return (
    <SelectedWebsiteContext.Provider value={{ websiteId, setWebsiteId }}>
      <div className="min-h-screen flex flex-col md:flex-row bg-brand-50">
        {/* ── Mobile top bar (visible < md) ──────────────────── */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100"
            aria-label={t("nav.openMenu")}
          >
            {/* Hamburger icon — inline SVG to avoid pulling in an
                icon library for a single glyph. */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="text-base font-semibold text-slate-900">
            AI Visibility
          </div>
          {/* Spacer to keep title centred between burger and a
              symmetric placeholder. */}
          <div className="w-9" />
        </header>

        {/* ── Backdrop for the mobile slide-out ──────────────── */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label={t("nav.closeMenu")}
            onClick={() => setSidebarOpen(false)}
            className="md:hidden fixed inset-0 z-30 bg-slate-900/40"
          />
        )}

        {/* ── Sidebar — fixed off-screen on mobile, static on md+ ── */}
        <aside
          className={[
            "bg-white border-r border-slate-200 flex flex-col",
            // Mobile: fixed, slides in from the left.
            "fixed top-0 left-0 z-40 h-full w-72 max-w-[85vw]",
            "transform transition-transform duration-200 ease-out",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
            // Desktop: static, always visible at 64-unit width.
            "md:static md:translate-x-0 md:w-64 md:h-auto md:max-w-none",
          ].join(" ")}
        >
          <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-3">
            <div>
              <span className="mono text-xs tracking-widest text-brand-700">
                SEOGEEKS
              </span>
              <div className="text-lg font-semibold text-slate-900 mt-1">
                AI Visibility
              </div>
            </div>
            {/* Close button on mobile only. */}
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="md:hidden -mr-2 p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
              aria-label={t("nav.closeMenu")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-3">
            <WebsitePicker onChange={setWebsiteId} />
            <TokenBalance />
          </div>

          <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
            {SECTIONS.map((s) => {
              const path = s.path.includes(":id")
                ? websiteId
                  ? s.path.replace(":id", String(websiteId))
                  : null
                : s.path;
              const disabled = path === null;
              return (
                <NavLink
                  key={s.key}
                  to={disabled ? "#" : path}
                  end={s.end}
                  onClick={(e) => disabled && e.preventDefault()}
                  className={({ isActive }) =>
                    [
                      "block rounded-md px-3 py-2 text-sm transition",
                      disabled
                        ? "text-slate-400 cursor-not-allowed"
                        : isActive
                          ? "bg-brand-100 text-brand-800 font-medium"
                          : "text-slate-700 hover:bg-brand-50",
                    ].join(" ")
                  }
                >
                  {t(`nav.${s.key}`)}
                </NavLink>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-200 space-y-3">
            <LanguageSwitcher className="w-full justify-center" />
            <button
              onClick={onLogout}
              className="w-full text-left text-sm text-slate-600 hover:text-slate-900"
            >
              {t("common.signOut")}
            </button>
            {/* Phase 6 footer — legal links opened in a new tab so
                they don't disturb the user's dashboard state. Hrefs
                include the /ai-visibility/ prefix explicitly because
                regular anchors bypass BrowserRouter's basename — the
                browser sees the literal href, not the routed path. */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 pt-2 border-t border-slate-100">
              <a
                href="/ai-visibility/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-900"
              >
                {t("legal.privacyShort")}
              </a>
              <a
                href="/ai-visibility/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-900"
              >
                {t("legal.termsShort")}
              </a>
              <a
                href="/ai-visibility/offer"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-900"
              >
                {t("legal.offerShort")}
              </a>
            </div>
          </div>
        </aside>

        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </SelectedWebsiteContext.Provider>
  );
}
